import { useDebouncedCallback } from '@mantine/hooks'
import { isDefaultBidderRuleFor } from '@pubstack/common/src/bidder-rule'
import { BulkProgress, chunk, DEPLOYMENT_CENTER_TABS, DeploymentCenterActionResult, DeploymentCenterTab, newVersionPlanStackIds } from '@pubstack/common/src/deployment-center'
import { CreateStack } from '@pubstack/common/src/stack'
import { isPubstackUserRole } from '@pubstack/common/src/rolesAndFeatures'
import { useQueryClient } from '@tanstack/react-query'
import { parseAsInteger, parseAsNumberLiteral, parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs'
import { FunctionComponent, useState } from 'react'
import { DEPLOYMENT_CENTER_QUERY_KEYS, fetchDeployPlan, fetchNewVersionPlan, runDeployPlan, runNewVersionPlan, useDeploymentCenterStacks } from '~/api/app/deployment-center.api'
import { SITES_QUERY_KEYS, useSitesWithPagination } from '~/api/app/sites.api'
import { useBidderRules } from '~/api/bidder-rules.api'
import { useBulkCreateStacks } from '~/api/stacks.api'
import { Colors } from '~/assets/style/colors'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { BaseInputElement } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { PAGE_LIMIT_OPTIONS, Pagination } from '~/components/Pagination'
import { label, prefix } from '~/components/layout/ProductTrackingIntegration'
import { TabProp, Tabs } from '~/components/Tabs'
import { useToast } from '~/components/Toast/useToasts'
import { Tooltip } from '~/components/Tooltip'
import { PageContent } from '~/components/PageContent'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useNodeStaffAccess } from '~/hooks/useNodeStaffAccess'
import { ConfirmationItem, ConfirmDeploymentModal } from './ConfirmDeploymentModal'
import { DeploymentCenterStackFlyout } from './DeploymentCenterStackFlyout'
import { DeploymentCenterMediaTable } from './DeploymentCenterMediaTable'
import { DeploymentCenterStackRow } from '@pubstack/common/src/deployment-center'
import { DeploymentCenterStackTable, StackSortBy } from './DeploymentCenterStackTable'
import { DeploymentSelection, EMPTY_SELECTION, offersWholeQueue, selectedCount, selectionOnly, selectWholeQueue, togglePage, toggleRow } from './deploymentCenterSelection'
import { DEPLOYMENT_TAB_LABEL, DEPLOYMENT_TAB_TRACKING, deploymentTabOfLabel, visibleDeploymentTabs } from './deploymentCenterTabs'

const ITEMS_PER_PAGE = 25
const SEARCH_DEBOUNCE_TIME = 300

const RELOAD_HINT = 'Reload the queue to see what is left.'

const stackCount = (n: number): string => `${n} stack${n === 1 ? '' : 's'}`

/** What each tab's button does, in the words on the button and in the modal above it. */
const ACTION_LABEL: Record<DeploymentCenterTab, string> = {
  'to-configure': 'Create default stacks',
  'out-of-sync': 'Create updated version',
  'to-deploy': 'Deploy',
}

const ACTION_DESCRIPTION: Record<DeploymentCenterTab, string> = {
  'to-configure': 'A default stack will be created on each of these medias. It lands ready to deploy — deploying it is a second step.',
  'out-of-sync': 'A new version will be created for each of these stacks, carrying the configuration changes they are missing. You still have to deploy them afterwards.',
  'to-deploy': 'These stacks will go live. Each new version replaces the newest version its media serves today and takes over that version’s share of the traffic.',
}

/** The base of each modal's tracking label — `ConfirmDeploymentModal` adds `-cancel`/`-confirm`. */
const ACTION_TRACKING: Record<DeploymentCenterTab, `${prefix}-${label}`> = {
  'to-configure': 'common-deployment-center-create-stacks-modal',
  'out-of-sync': 'common-deployment-center-create-updated-versions-modal',
  'to-deploy': 'common-deployment-center-deploy-modal',
}

/**
 * One deployment center for both products, rowed by stack group.
 *
 * It replaces two identically-named pages — one per product, differing only by the URL prefix
 * `useProduct` read back — each stacking four always-open sections that a customer had to scroll past to
 * find the work. What is left is a queue: three tabs, each holding the stacks that need one specific
 * thing done to them, and one button that does it.
 *
 * The page is built for the size of the job rather than for the size of a screen. A large scope has a
 * thousand pending stacks, and everything expensive about that is kept off the client: the rows are
 * paginated and derived server-side, the tab counts come out of the same aggregation as the rows, and a
 * bulk action sends **the query** rather than a list of ids — so "deploy everything" is one planning
 * request whatever the queue holds, applied in chunks the 29s gateway limit can survive.
 *
 * Products are not tabs here. Which stacks a viewer may see is decided server-side by
 * `deploymentCenterStackTypes`, so a One customer without Node never receives Node rows *or* counts for
 * them, and no client-side filter can widen that.
 */
export const DeploymentCenterPage: FunctionComponent = () => {
  const user = useAuthenticatedUser()
  const { hasNode, isStaffOnNode } = useNodeStaffAccess()
  const scopeId = user.currentScopeId
  const breadcrumbs = useBreadcrumbs()
  const modal = useGlobalModal()
  const flyout = useGlobalFlyout()
  const toast = useToast()
  const isPubstackUser = isPubstackUserRole(user.userRoles)
  const queryClient = useQueryClient()

  // Entitlement is the account's, not the viewer's: `user.hasFeature` synthesises every feature for
  // Pubstack staff, which would show a One-only customer's page a tab their scope has no use for. Staff on a
  // scope onboarded on Node but not yet opened to its users do get the Node tab, marked with a shield.
  const visibleTabs = visibleDeploymentTabs({ hasNodeAccess: hasNode })

  const [tabParam, setTab] = useQueryState<DeploymentCenterTab>('tab', parseAsStringLiteral(DEPLOYMENT_CENTER_TABS).withOptions({ history: 'push' }))
  const [page, setPage] = useQueryState<number>('page', parseAsInteger.withDefault(1).withOptions({ clearOnDefault: false }))
  const [limit, setLimit] = useQueryState<number>('limit', parseAsNumberLiteral(PAGE_LIMIT_OPTIONS).withDefault(ITEMS_PER_PAGE).withOptions({ clearOnDefault: false }))
  const [search, setSearch] = useQueryState<string>('search', parseAsString.withDefault(''))
  const [sortBy, setSortBy] = useQueryState<StackSortBy>(
    'sortBy',
    parseAsStringLiteral(['mediaName', 'stackName', 'latestVersion'] as const)
      .withDefault('mediaName')
      .withOptions({ clearOnDefault: false })
  )
  const [sortOrder, setSortOrder] = useQueryState<'ascending' | 'descending'>(
    'sortOrder',
    parseAsStringLiteral(['ascending', 'descending'] as const)
      .withDefault('ascending')
      .withOptions({ clearOnDefault: false })
  )

  // A `?tab=` the viewer cannot see falls back rather than rendering an empty tab or a refusal — the URL
  // is shared between colleagues on different scopes, and a stale one should still land somewhere useful.
  const tab = tabParam && visibleTabs.includes(tabParam) ? tabParam : visibleTabs[0]

  // Tab, page, limit and search live in the URL, so they also change without a click — browser
  // back/forward restores them in place. The selection is therefore keyed to the query that produced it
  // and evaporates the moment any part changes: a selection made in one queue must never reach another.
  const selectionKey = `${tab}|${page}|${limit}|${search}`
  const [selectionState, setSelectionState] = useState({ key: selectionKey, selection: EMPTY_SELECTION })
  const selection = selectionState.key === selectionKey ? selectionState.selection : EMPTY_SELECTION
  const setSelection = (next: DeploymentSelection) => setSelectionState({ key: selectionKey, selection: next })
  const [localSearch, setLocalSearch] = useState(search)
  const [progress, setProgress] = useState<BulkProgress | null>(null)

  // The stack queues answer both their own rows and *both* tab counts, so the query runs even while To
  // configure is open — otherwise switching to it would blank the two badges beside it.
  const {
    data: { stacks, counts },
    isLoading: isLoadingStacks,
  } = useDeploymentCenterStacks({ scopeId, tab: tab === 'to-configure' ? 'to-deploy' : tab, search, page, limit, sortBy, sortOrder })

  // To configure is the one media-rowed tab, and it is still served by the sites endpoint: its rows are
  // medias Node knows about and has no stack for, which is what `controlCenterStatus` already answers.
  const { data: medias, isLoading: isLoadingMedias } = useSitesWithPagination({
    scopeId,
    filters: { controlCenterStatus: 'to-configure', search },
    pagination: { page, limit },
    sortBy: 'name',
    sortOrder: 'ascending',
    enabled: visibleTabs.includes('to-configure'),
  })

  const { data: bidderRules, isLoading: isLoadingBidderRules } = useBidderRules('pbs', false, visibleTabs.includes('to-configure'))
  const { mutateAsync: bulkCreateStacks } = useBulkCreateStacks()

  const isMediaTab = tab === 'to-configure'
  const isLoading = isMediaTab ? isLoadingMedias || isLoadingBidderRules : isLoadingStacks
  const pagination = isMediaTab ? medias.pagination : stacks.pagination
  // Every listed row is actionable. An A/B-deployed group used to be barred from "Create updated version"
  // and rendered a disabled checkbox; that guard now sits on the deploy, which is the half that moves
  // traffic, so there is nothing left on this page for a row to be ineligible for.
  const pageIds = isMediaTab ? medias.data.map((media) => media.id) : stacks.data.map((row) => row.stackGroupId)
  const queueTotal = pagination.total
  const count = selectedCount(selection, queueTotal)

  const tabCounts: Record<DeploymentCenterTab, number> = { 'to-configure': medias.pagination.total, 'out-of-sync': counts['out-of-sync'], 'to-deploy': counts['to-deploy'] }

  const tabs: TabProp[] = visibleTabs.map((visible) => ({
    label: DEPLOYMENT_TAB_LABEL[visible],
    trackingLabel: DEPLOYMENT_TAB_TRACKING[visible],
    active: visible === tab,
    count: tabCounts[visible],
    ...(isStaffOnNode && visible === 'to-configure' ? { iconName: 'admin' as const, iconFill: Colors.SlateGrey } : {}),
  }))

  const resetSelection = () => setSelection(EMPTY_SELECTION)

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, SEARCH_DEBOUNCE_TIME)

  /**
   * Open the stack behind a row.
   *
   * The **latest** version, which is the one every column describes and the one both actions write. The
   * flyout shows it against what the group is currently serving, so it is also where the drift a row is
   * listed for can actually be read.
   */
  const handleOpenStack = (row: DeploymentCenterStackRow) => {
    const latest = row.versions[0]
    if (!latest) {
      return
    }
    flyout.open(DeploymentCenterStackFlyout, { color: Colors.Ming, closeTrackingLabel: 'common-deployment-center-stack-close' }, { stackId: latest.id, siteId: row.siteId, scopeId })
  }

  const handleTabClick = (clicked: TabProp) => {
    const next = deploymentTabOfLabel(clicked.label)
    if (!next || next === tab) {
      return
    }
    setTab(next)
    setPage(1)
  }

  /**
   * The per-row detail in the confirmation, and only for a selection small enough to read.
   *
   * A whole-queue run gets `undefined`: the rows are not on the client, and fetching a thousand of them
   * to print in a modal would undo the reason the action is query-shaped in the first place.
   */
  const confirmationItems = (): ConfirmationItem[] | undefined => {
    if (selection.kind === 'queue') {
      return undefined
    }
    const ids = new Set(selection.ids)
    return isMediaTab
      ? medias.data.filter((media) => ids.has(media.id)).map((media) => ({ stackGroupId: media.id, mediaName: media.name, stackName: 'Default', latestVersion: 1 }))
      : stacks.data.filter((row) => ids.has(row.stackGroupId)).map((row) => ({ stackGroupId: row.stackGroupId, mediaName: row.mediaName, stackName: row.stackName, latestVersion: row.latestVersion }))
  }

  const run = async (action: () => Promise<DeploymentCenterActionResult>) => {
    modal.close()
    setProgress({ phase: 'applying', done: 0, total: 0 })
    try {
      const result = await action()
      const skipped = result.skipped.length > 0 ? `, ${result.skipped.length} skipped` : ''
      if (result.stopped) {
        // Neither a success nor a total loss, so it reports both halves. The figures are exact because
        // no group is ever left half-written.
        toast.alert(`The run stopped before it finished: ${stackCount(result.succeeded)} updated${skipped}, ${result.stopped.notAttempted} not attempted. ${RELOAD_HINT}`)
      } else {
        toast.success(`${stackCount(result.succeeded)} updated${skipped}.`)
      }
    } catch {
      // For the runs that throw rather than report how far they got. Earlier chunks are already
      // applied, and the queue is self-describing, so re-reading it beats a rollback.
      toast.alert(`The run stopped before it finished. ${RELOAD_HINT}`)
    } finally {
      setProgress(null)
      resetSelection()
      await Promise.all([queryClient.invalidateQueries({ queryKey: DEPLOYMENT_CENTER_QUERY_KEYS(scopeId) }), queryClient.invalidateQueries({ queryKey: SITES_QUERY_KEYS(scopeId) })])
    }
  }

  /** Create the Node `Default` stack on each selected media — the one creation that can be done in bulk. */
  const createDefaultStacks = async (siteIds: string[], defaultBidderRuleId: string): Promise<DeploymentCenterActionResult> => {
    const toCreate: CreateStack[] = siteIds.map((siteId) => ({ type: 'pbs', name: 'Default', siteId, configuration: { contextId: null, bidderRuleId: defaultBidderRuleId }, priority: 1 }))
    for (const batch of chunk(toCreate)) {
      await bulkCreateStacks(batch)
    }
    return { resolved: siteIds.length, succeeded: siteIds.length, skipped: [] }
  }

  /**
   * `abDeployed` is the deploy plan's own count, and only the deploy passes one: it is the sole action that
   * moves traffic — creating a version writes a pending document and touches no live one. Counted on the
   * server because a whole-queue run never holds its rows, which is exactly the run where the number
   * matters most.
   */
  const confirm = (total: number, skipped: DeploymentCenterActionResult['skipped'], action: () => Promise<DeploymentCenterActionResult>, abDeployed?: number) =>
    modal.open(ConfirmDeploymentModal, {
      actionLabel: ACTION_LABEL[tab],
      description: ACTION_DESCRIPTION[tab],
      total,
      skipped,
      abDeployed,
      items: confirmationItems(),
      trackingLabel: ACTION_TRACKING[tab],
      onCancel: () => modal.close(),
      onConfirm: () => run(action),
    })

  /**
   * Resolve the run on the server, then confirm it, then apply it.
   *
   * Planning first is what lets the confirmation state a number the server stands behind rather than the
   * one the badge happened to show: the queue is live, and stacks drift into and out of it between a
   * render and a click.
   */
  const handleAction = async () => {
    if (progress) {
      return
    }
    setProgress({ phase: 'collecting', done: 0, total: 0 })
    try {
      if (isMediaTab) {
        // Checked before the modal opens, not inside the run: a scope with no Default rule cannot create
        // these stacks at all, and saying so up front is a different message from a run that stopped
        // partway through.
        const defaultBidderRule = bidderRules.find((rule) => isDefaultBidderRuleFor(rule, 'pbs'))
        if (!defaultBidderRule) {
          toast.alert('Could not create the stacks: this scope has no default bidder rule.')
          return
        }
        const siteIds = selection.kind === 'queue' ? pageIds : selection.ids
        confirm(siteIds.length, [], () => createDefaultStacks(siteIds, defaultBidderRule.id))
        return
      }

      const request = { search: search || undefined, only: selectionOnly(selection) }
      if (tab === 'to-deploy') {
        const plan = await fetchDeployPlan(request)
        confirm(plan.resolved - plan.skipped.length, plan.skipped, () => runDeployPlan(plan, setProgress), plan.abDeployed)
      } else {
        const plan = await fetchNewVersionPlan(request)
        confirm(newVersionPlanStackIds(plan).length, plan.skipped, () => runNewVersionPlan(plan, setProgress))
      }
    } catch {
      toast.alert('Could not work out what this action would change. Try again.')
    } finally {
      setProgress(null)
    }
  }

  const actionLabel = progress?.phase === 'applying' && progress.total > 0 ? `${ACTION_LABEL[tab]} (${progress.done}/${progress.total})` : ACTION_LABEL[tab]

  return (
    <PageContent breadcrumbs={breadcrumbs}>
      <div className={'flex flex-col gap-4'}>
        <Tabs tabs={tabs} onClick={handleTabClick} />

        <div className={'flex items-center justify-between gap-4'}>
          <BaseInputElement
            type={'text'}
            iconLeft={'search'}
            labelIsPlaceholder
            label={'Search a media'}
            value={localSearch}
            onChange={(event) => {
              setLocalSearch(event.target.value)
              debouncedSearch(event.target.value)
            }}
          />
          {/* A disabled primary with nothing said about it reads as broken rather than as waiting. The
              reason is always the same one — nothing is ticked — so the tooltip says it, and steps
              aside once the button is live or busy, when there is nothing to explain. */}
          <Tooltip title={`Select at least one ${isMediaTab ? 'media' : 'stack'} first.`} disabled={count > 0 || !!progress || isLoading}>
            <span>
              <Button
                variant={'primary'}
                disabled={count === 0 || !!progress || isLoading}
                iconName={progress ? 'pubstack' : undefined}
                iconSpin={!!progress}
                onClick={handleAction}
                trackingLabel={'common-deployment-center-action-apply'}
              >
                {count > 0 ? `${actionLabel} (${count})` : actionLabel}
              </Button>
            </span>
          </Tooltip>
        </div>

        {/* The whole-queue offer. Without it the header checkbox is a promise the page cannot keep at a
            thousand rows — it selects a page, and the run would quietly cover only that page. */}
        {!isMediaTab && offersWholeQueue(selection, pageIds, queueTotal) && (
          <div className={'flex items-center justify-center gap-2 rounded bg-topaze p-2'}>
            <span>All {pageIds.length} stacks on this page are selected.</span>
            <Button variant={'tertiary'} onClick={() => setSelection(selectWholeQueue())} trackingLabel={'common-deployment-center-select-queue-set'}>
              Select all {queueTotal} in this queue
            </Button>
          </div>
        )}

        {isMediaTab ? (
          <DeploymentCenterMediaTable
            medias={medias.data}
            isLoading={isLoading}
            selection={selection}
            onToggleRow={(siteId) => setSelection(toggleRow(selection, siteId, pageIds))}
            onTogglePage={() => setSelection(togglePage(selection, pageIds))}
          />
        ) : (
          <DeploymentCenterStackTable
            rows={stacks.data}
            isLoading={isLoading}
            selection={selection}
            scopeId={scopeId}
            isPubstackUser={isPubstackUser}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onOpenStack={handleOpenStack}
            onToggleRow={(stackGroupId) => setSelection(toggleRow(selection, stackGroupId, pageIds))}
            onTogglePage={() => setSelection(togglePage(selection, pageIds))}
            onSortChange={(nextSortBy, nextSortOrder) => {
              setSortBy(nextSortBy)
              setSortOrder(nextSortOrder)
              setPage(1)
            }}
          />
        )}

        <Pagination
          currentPage={page}
          totalPages={pagination.totalPages}
          limit={limit}
          totalItems={pagination.total}
          onPageChange={(next) => setPage(next)}
          onLimitChange={(next) => {
            setLimit(next)
            setPage(1)
          }}
        />
      </div>
    </PageContent>
  )
}
